/**
 * Country dialling codes for the player phone-number field.
 *
 * `nsn` (national significant number length) is set ONLY where the country has a
 * single, well-known fixed length — today that is Poland, the default. For every
 * other country we validate loosely (min 5 digits, E.164 max 15 digits including
 * the dialling code) instead of guessing per-country rules and rejecting valid
 * foreign numbers.
 *
 * Country names come from `Intl.DisplayNames` in the active UI locale, so the
 * list is not duplicated per language.
 */

export interface Country {
  /** ISO 3166-1 alpha-2 code — unique key (dial codes are NOT unique, e.g. US/CA share +1). */
  iso: string;
  /** Dialling code with leading plus, e.g. "+48". */
  dial: string;
  /** Exact national number length, when the country has one. */
  nsn?: number;
}

export const DEFAULT_COUNTRY_ISO = 'PL';

/** Minimum national number length accepted for countries without a fixed `nsn`. */
export const MIN_NSN_LENGTH = 5;

/** E.164 hard limit: dialling code + national number. */
export const MAX_E164_DIGITS = 15;

export const COUNTRIES: Country[] = [
  { iso: 'PL', dial: '+48', nsn: 9 },
  { iso: 'AL', dial: '+355' },
  { iso: 'AD', dial: '+376' },
  { iso: 'AE', dial: '+971' },
  { iso: 'AR', dial: '+54' },
  { iso: 'AT', dial: '+43' },
  { iso: 'AU', dial: '+61' },
  { iso: 'BA', dial: '+387' },
  { iso: 'BE', dial: '+32' },
  { iso: 'BG', dial: '+359' },
  { iso: 'BR', dial: '+55' },
  { iso: 'BY', dial: '+375' },
  { iso: 'CA', dial: '+1' },
  { iso: 'CH', dial: '+41' },
  { iso: 'CL', dial: '+56' },
  { iso: 'CN', dial: '+86' },
  { iso: 'CO', dial: '+57' },
  { iso: 'CY', dial: '+357' },
  { iso: 'CZ', dial: '+420' },
  { iso: 'DE', dial: '+49' },
  { iso: 'DK', dial: '+45' },
  { iso: 'EE', dial: '+372' },
  { iso: 'EG', dial: '+20' },
  { iso: 'ES', dial: '+34' },
  { iso: 'FI', dial: '+358' },
  { iso: 'FR', dial: '+33' },
  { iso: 'GB', dial: '+44' },
  { iso: 'GE', dial: '+995' },
  { iso: 'GR', dial: '+30' },
  { iso: 'HK', dial: '+852' },
  { iso: 'HR', dial: '+385' },
  { iso: 'HU', dial: '+36' },
  { iso: 'IE', dial: '+353' },
  { iso: 'IL', dial: '+972' },
  { iso: 'IN', dial: '+91' },
  { iso: 'IS', dial: '+354' },
  { iso: 'IT', dial: '+39' },
  { iso: 'JP', dial: '+81' },
  { iso: 'KR', dial: '+82' },
  { iso: 'KZ', dial: '+7' },
  { iso: 'LI', dial: '+423' },
  { iso: 'LT', dial: '+370' },
  { iso: 'LU', dial: '+352' },
  { iso: 'LV', dial: '+371' },
  { iso: 'MA', dial: '+212' },
  { iso: 'MC', dial: '+377' },
  { iso: 'MD', dial: '+373' },
  { iso: 'ME', dial: '+382' },
  { iso: 'MK', dial: '+389' },
  { iso: 'MT', dial: '+356' },
  { iso: 'MX', dial: '+52' },
  { iso: 'NL', dial: '+31' },
  { iso: 'NO', dial: '+47' },
  { iso: 'NZ', dial: '+64' },
  { iso: 'PH', dial: '+63' },
  { iso: 'PT', dial: '+351' },
  { iso: 'QA', dial: '+974' },
  { iso: 'RO', dial: '+40' },
  { iso: 'RS', dial: '+381' },
  { iso: 'RU', dial: '+7' },
  { iso: 'SA', dial: '+966' },
  { iso: 'SE', dial: '+46' },
  { iso: 'SG', dial: '+65' },
  { iso: 'SI', dial: '+386' },
  { iso: 'SK', dial: '+421' },
  { iso: 'SM', dial: '+378' },
  { iso: 'TH', dial: '+66' },
  { iso: 'TN', dial: '+216' },
  { iso: 'TR', dial: '+90' },
  { iso: 'UA', dial: '+380' },
  { iso: 'US', dial: '+1' },
  { iso: 'VN', dial: '+84' },
  { iso: 'ZA', dial: '+27' },
];

const COUNTRY_BY_ISO = new Map(COUNTRIES.map((c) => [c.iso, c]));

export function getCountry(iso: string): Country {
  return COUNTRY_BY_ISO.get(iso) ?? COUNTRY_BY_ISO.get(DEFAULT_COUNTRY_ISO)!;
}

/** Flag emoji built from the ISO code (regional indicator symbols). */
export function flagEmoji(iso: string): string {
  return String.fromCodePoint(
    ...iso
      .toUpperCase()
      .split('')
      .map((char) => 0x1f1e6 + char.charCodeAt(0) - 65)
  );
}

/** Localized country name, falling back to the ISO code when Intl is unavailable. */
export function countryName(iso: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(iso) ?? iso;
  } catch {
    return iso;
  }
}

/** Max national digits for a country — never lets the E.164 total exceed 15. */
export function maxNsnLength(country: Country): number {
  const dialDigits = country.dial.replace(/\D/g, '').length;
  const room = MAX_E164_DIGITS - dialDigits;
  return country.nsn ? Math.min(country.nsn, room) : room;
}

/** Countries ordered for the picker: default first, then alphabetically in `locale`. */
export function sortedCountries(locale: string): Country[] {
  const collator = new Intl.Collator(locale);
  return [...COUNTRIES].sort((a, b) => {
    if (a.iso === DEFAULT_COUNTRY_ISO) return -1;
    if (b.iso === DEFAULT_COUNTRY_ISO) return 1;
    return collator.compare(countryName(a.iso, locale), countryName(b.iso, locale));
  });
}
